import axios from "axios";
import { apiVariable } from "./apiVariable";

export type BlacklistScope = "C1" | "Fox";
export interface BrokerBlacklistWrite {
  list: BlacklistScope;
  name: string;
  address?: string | null;
  email?: string | null;
  domain?: string | null;
  mcNumber?: string | null;
  dotNumber?: string | null;
  reason?: string | null;
  sourceEmail?: string | null;
}
export interface BrokerBlacklistEntry extends BrokerBlacklistWrite {
  id: string;
  updatedAtUtc: string;
}
export interface BrokerCheck {
  list?: BlacklistScope;
  name?: string;
  email?: string;
  domain?: string;
  mcNumber?: string;
  dotNumber?: string;
}
export interface BrokerMatch {
  entry: BrokerBlacklistEntry;
  matchedOn: string[];
}
export interface ImportPreview {
  entries: BrokerBlacklistWrite[];
  errors: string[];
}
export interface ImportResult {
  imported: number;
  duplicates: number;
  errors: string[];
}
const endpoint = `${apiVariable}/broker-blacklist`;
const config = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});
export const brokerBlacklistService = {
  get: async (token: string, list: BlacklistScope) =>
    (
      await axios.get<BrokerBlacklistEntry[]>(endpoint, {
        ...config(token),
        params: { list },
      })
    ).data,
  save: async (token: string, entry: BrokerBlacklistWrite, id?: string) =>
    (
      await (id
        ? axios.put<BrokerBlacklistEntry>(
            `${endpoint}/${id}`,
            entry,
            config(token),
          )
        : axios.post<BrokerBlacklistEntry>(endpoint, entry, config(token)))
    ).data,
  remove: async (token: string, id: string) =>
    axios.delete(`${endpoint}/${id}`, config(token)),
  check: async (token: string, input: BrokerCheck, signal?: AbortSignal) =>
    (
      await axios.post<BrokerMatch[]>(`${endpoint}/check`, input, {
        ...config(token),
        signal,
      })
    ).data,
  preview: async (token: string, list: BlacklistScope, file: File) => {
    const form = new FormData();
    form.append("list", list);
    form.append("file", file);
    return (
      await axios.post<ImportPreview>(
        `${endpoint}/import/preview`,
        form,
        config(token),
      )
    ).data;
  },
  import: async (
    token: string,
    list: BlacklistScope,
    entries: BrokerBlacklistWrite[],
  ) =>
    (
      await axios.post<ImportResult>(
        `${endpoint}/import`,
        { list, entries },
        config(token),
      )
    ).data,
};
export const blacklistError = (error: unknown) => {
  if (axios.isAxiosError(error) && typeof error.response?.data === "string")
    return error.response.data;
  return "Unable to complete the blacklist request. Please try again.";
};
